import { assets } from 'config/assets';
import type { GetSiteImagesHandlerResponse, SelectableImage, SiteImagesSource } from 'types/site-images';

function toSelectableImage(id: string, imageUrl: string, thumbnailUrl = imageUrl): SelectableImage {
  return { id, imageUrl, thumbnailUrl };
}

function appendUniqueImages(target: SelectableImage[], candidates: SelectableImage[]): void {
  const seenUrls = new Set(target.map((image) => image.imageUrl));

  for (const candidate of candidates) {
    if (seenUrls.has(candidate.imageUrl)) {
      continue;
    }

    seenUrls.add(candidate.imageUrl);
    target.push(candidate);
  }
}

export function getStaticSelectableImages(): SelectableImage[] {
  return assets.map((asset, index) => toSelectableImage(`static-${index}`, asset.imageUrl));
}

export function buildSelectableImagesFromSiteResponse(response: GetSiteImagesHandlerResponse): SelectableImage[] {
  const images: SelectableImage[] = [];

  if (response.logoUrl) {
    images.push(toSelectableImage('site-logo', response.logoUrl));
  }

  appendUniqueImages(
    images,
    response.mediaItems.map((item) => toSelectableImage(item.id, item.url, item.thumbnailUrl)),
  );

  return images;
}

export function resolveSelectableImages(
  isExistingSiteMode: boolean,
  siteImages?: GetSiteImagesHandlerResponse,
  hasSiteImagesError = false,
): SelectableImage[] {
  if (!isExistingSiteMode) {
    return getStaticSelectableImages();
  }

  if (siteImages) {
    const userImages = buildSelectableImagesFromSiteResponse(siteImages);

    if (userImages.length > 0) {
      const merged = [...userImages];
      appendUniqueImages(merged, getStaticSelectableImages());
      return merged;
    }
  }

  if (hasSiteImagesError || siteImages?.source === 'empty') {
    return getStaticSelectableImages();
  }

  return [];
}

export function resolveImagesSource(
  isExistingSiteMode: boolean,
  siteImages?: GetSiteImagesHandlerResponse,
  hasSiteImagesError = false,
): SiteImagesSource | 'static' | undefined {
  if (!isExistingSiteMode) {
    return undefined;
  }

  if (hasSiteImagesError || siteImages?.source === 'empty') {
    return 'static';
  }

  return siteImages?.source;
}
